import React from 'react';

import { getMinimumSiteSubscriptionForFeatures, SITE_FEATURES } from '@gitbook/app-pricing';
import { Dialog, DialogBrandedLayout, DialogRoot } from '@gitbook/spine-react';
import { assertIsDefined } from '@gitbook/util-ts';

import { SitePricingModalAside } from '../../SitePricingModalAside';
import { useSiteUpgradeModal } from '../useSiteUpgradeModal';
import { SiteUpgradeModalStepBilling } from './SiteUpgradeModalStepBilling';
import { SiteUpgradeModalStepFeature } from './SiteUpgradeModalStepFeature';

/**
 * A modal for upgrading a site. It is controlled through the siteUpgradeModalState atom.
 * It only handles sites on new pricing.
 */
export function SiteUpgradeModal() {
    const { close, isOpened } = useSiteUpgradeModal();

    return (
        <DialogRoot
            isOpen={isOpened}
            onOpenChange={(isOpen) => {
                if (!isOpen) {
                    close();
                }
            }}
        >
            <ModalBody />
        </DialogRoot>
    );
}

function ModalBody() {
    const { upgradeState, site } = useSiteUpgradeModal();

    if (!upgradeState || !site) {
        return null;
    }

    const featureTrigger = upgradeState?.reason
        ? SITE_FEATURES.find((feature) => feature.key === upgradeState.reason)
        : undefined;
    const requiredSiteType = (() => {
        if (upgradeState?.type) {
            return upgradeState.type;
        }

        if (featureTrigger) {
            return getMinimumSiteSubscriptionForFeatures({
                isNewPricing: true,
                featureKeys: [featureTrigger.key],
                defaultSiteType: site.type,
            });
        }
    })();

    // The modal cannot show without a required site type.
    assertIsDefined(requiredSiteType);

    return (
        <Dialog size="auto">
            <DialogBrandedLayout
                aside={{
                    content: (
                        <React.Suspense fallback={null}>
                            <SitePricingModalAside requiredSiteType={requiredSiteType} />
                        </React.Suspense>
                    ),
                }}
            >
                {upgradeState?.tab === 'plans' ? (
                    <SiteUpgradeModalStepFeature
                        featureTrigger={featureTrigger}
                        targetSiteType={requiredSiteType}
                    />
                ) : null}
                {upgradeState?.tab === 'billing' ? (
                    <SiteUpgradeModalStepBilling upgradeState={upgradeState} />
                ) : null}
            </DialogBrandedLayout>
        </Dialog>
    );
}
